import React from 'react';
import { TrendingUp, Award, Droplets, FlaskConical, Wrench, Settings } from 'lucide-react';
import { useTranslation } from '../translations';

const ScoreBreakdown = ({ groupScores, totalScore }) => {
  const { t } = useTranslation();
  
  // Detectar versão v1 ou v2
  const isV2 = groupScores?.version === 'v2' || groupScores?.B1 !== undefined;
  
  // Grupos para v2 (14 critérios, 4 grupos)
  const groupsV2 = [
    {
      key: 'A',
      label: t('tappingTechnique'),
      icon: Award,
      color: 'blue',
      max: 45,
      score: groupScores?.A || 0
    },
    {
      key: 'B1',
      label: t('harvestPreparation'),
      icon: Settings,
      color: 'green',
      max: 20,
      score: groupScores?.B1 || 0
    },
    {
      key: 'B2',
      label: t('dailyMaintenance'),
      icon: Wrench,
      color: 'amber',
      max: 15,
      score: groupScores?.B2 || 0
    },
    {
      key: 'C',
      label: t('chemicalApplications'),
      icon: FlaskConical,
      color: 'purple',
      max: 20,
      score: groupScores?.C || 0
    }
  ];
  
  // Grupos para v1 (10 critérios, 3 grupos + privado)
  const groupsV1 = [
    {
      key: 'A',
      label: t('tappingTechnique'),
      icon: Award,
      color: 'blue',
      max: 40,
      score: groupScores?.A || 0
    },
    {
      key: 'B',
      label: t('maintenanceAndCare'),
      icon: TrendingUp,
      color: 'green',
      max: 30,
      score: groupScores?.B2 || groupScores?.B || 0
    },
    {
      key: 'C',
      label: t('chemicalApplications'),
      icon: FlaskConical,
      color: 'purple',
      max: 20,
      score: groupScores?.C || 0
    },
    {
      key: 'D',
      label: t('productivity'),
      icon: Droplets,
      color: 'gray',
      max: 10,
      score: groupScores?.D || null
    }
  ];
  
  const groups = isV2 ? groupsV2 : groupsV1;

  const colorClasses = {
    blue: {
      bg: 'bg-blue-50',
      text: 'text-blue-600',
      bar: 'bg-blue-500'
    },
    green: {
      bg: 'bg-green-50',
      text: 'text-green-600',
      bar: 'bg-green-500'
    },
    amber: {
      bg: 'bg-amber-50',
      text: 'text-amber-600',
      bar: 'bg-amber-500'
    },
    purple: {
      bg: 'bg-purple-50',
      text: 'text-purple-600',
      bar: 'bg-purple-500'
    },
    gray: {
      bg: 'bg-gray-50',
      text: 'text-gray-600',
      bar: 'bg-gray-500'
    }
  };

  return (
    <div className="space-y-4">
      {/* Total Score */}
      <div className="text-center mb-6">
        <div className="text-6xl font-bold text-blue-600 mb-2">
          {totalScore}
        </div>
        <div className="text-gray-600 text-lg">{t('outOf100Points')}</div>
      </div>

      {/* Group Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {groups.map((group) => {
          const colors = colorClasses[group.color];
          const Icon = group.icon;
          const percentage = group.score !== null ? (group.score / group.max) * 100 : 0;
          const isPrivate = group.key === 'D' && group.score === null;

          return (
            <div 
              key={group.key}
              className={`p-4 rounded-lg border ${colors.bg} transition-all hover:shadow-md`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Icon className={`w-5 h-5 ${colors.text}`} />
                  <span className="font-medium text-gray-700">{group.label}</span>
                </div>
                <span className={`text-2xl font-bold ${colors.text}`}>
                  {isPrivate ? '🔒' : `${group.score}/${group.max}`}
                </span>
              </div>
              
              {!isPrivate && (
                <>
                  {/* Progress Bar */}
                  <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                    <div
                      className={`h-2 rounded-full ${colors.bar} transition-all duration-500`}
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  <div className="text-xs text-gray-500 text-right">
                    {percentage.toFixed(0)}%
                  </div>
                </>
              )}
              
              {isPrivate && (
                <div className="text-xs text-gray-500 text-center mt-2">
                  {t('privateScore')}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ScoreBreakdown;